import {
    type ApiEndpoint,
    type DownloadUrlResponse,
    isDownloadUrlResponse,
} from "@/shared/api-endpoint";
import type {
    MagicFitAdvancedField,
    MagicFitAdvancedParams,
} from "@/shared/magic-fit-advanced.api";
import { kPitchPaletteLength } from "@/shared/modal.api";
import type { ParameterRange } from "@/shared/parameter-range";
import { isObject } from "@/shared/utils/typeGuards";

export const kMagicFit30Route = "/magic-fit-3.0";

export { kPitchPaletteLength };

// Magic Fit uses its own complexity default, unlike Variations 3.0 / Inpainting
// which share kComplexity30Range from modal.api.
export const kComplexity30Range: ParameterRange = { min: 0, max: 1, default: 0.69 };

export const kVariationRange: ParameterRange = { min: 0, max: 1, default: 0.09 };
export const kBalanceRange: ParameterRange = { min: 0, max: 1, default: 0.27 };

/**
 * Advanced mode's bounds, mirroring `AdvancedParams` in the Modal app. Each
 * default is what the default sliders above amount to; gamma and its decay
 * power are `balance_to_params(0.27)` there, so they go stale if either the
 * Balance default or that function changes.
 */
/**
 * Sampler steps per request, fixed in the Modal app. Start and stop timesteps
 * resolve to step indices over this many steps, and the gamma decay compounds
 * once per step, so the guidance envelope drawn in the client depends on it.
 */
export const kMagicFit30SamplerSteps = 20;

export const kMagicFit30AdvancedRanges: Record<MagicFitAdvancedField, ParameterRange> = {
    inv_gamma: { min: 0, max: 1, default: kVariationRange.default },
    gamma: { min: 0, max: 1, default: 0.8960869565217392 },
    gamma_decay_power: { min: 0, max: 10000, default: 1.0610910025134217 },
    start_timestep: { min: 0, max: 1, default: 0 },
    stop_timestep: { min: 0, max: 1, default: 0.75 },
    naturalness: { min: 0, max: 1, default: kBalanceRange.default },
    complexity: { min: 0, max: 1, default: kComplexity30Range.default },
};

export interface MagicFit30RequestBody {
    drivingAudioKey: string;
    timbreAudioKey: string;
    bpm: number;
    pitchPalette?: number[];
    variation?: number;
    balance?: number;
    complexity?: number;
    /** Advanced mode. A request carrying it leaves out the three fields above. */
    advanced?: MagicFitAdvancedParams;
}

export const isMagicFit30RequestBody = (value: unknown): value is MagicFit30RequestBody =>
    isObject(value) &&
    typeof value.drivingAudioKey === "string" &&
    typeof value.timbreAudioKey === "string" &&
    typeof value.bpm === "number";

export const kMagicFit30Endpoint: ApiEndpoint<MagicFit30RequestBody, DownloadUrlResponse> = {
    route: kMagicFit30Route,
    label: "Magic Fit 3.0",
    source: "Modal",
    category: "SpliceGen v3.0",
    description:
        "Magic Fit using the SpliceGen 3.0 model with complexity control over the timbre transfer.",
    isRequestBody: isMagicFit30RequestBody,
    isSuccessResponse: isDownloadUrlResponse,
};
